import { useMemo } from "react";
import { useMemoDetail } from "@/hooks/useMemo";
import { useSeries } from "@/hooks/useMeetingSeries";
import { useWorkspaceMembers } from "@/hooks/useWorkspaceMembers";
import { memoPeopleById } from "@/lib/memoPeople";
import { buildPrintDoc, type PrintDoc } from "@/lib/memoPrint";
import type { MemoUser, SectionHistoryEntry, UUID, WorkspaceMember } from "@/lib/types";

export interface PrintMemoState {
  doc: PrintDoc | null;
  ready: boolean;
  isError: boolean;
}

/**
 * Assembles everything the print page needs from a memo id: the memo itself and the
 * people map (mirroring `MemoPage`'s assignment-pool union, not the attendance
 * roster). The `ready` flag gates auto-print — it flips true once the memo loads.
 *
 * 🔴 The print is DELIBERATELY LEAN: it shows the memo's OWN entries — open items
 * plus closures made in THIS meeting — and does NOT fold in the cross-week
 * `frühere Punkte` history (a screen-only reveal). Prior meetings' closed items are
 * dropped at carry-forward (`carry_decision` → "drop"), so a memo's own entries are
 * already exactly that lean set; we simply pass an empty history map to
 * `buildPrintDoc`. This keeps a weekly protocol a page or two instead of dumping
 * every resolved line from months of meetings onto paper.
 */
export function usePrintMemo(memoId: UUID | undefined): PrintMemoState {
  const { data: memo, isLoading } = useMemoDetail(memoId);
  const { data: series } = useSeries(memo?.series_id);
  const { data: workspaceMembers = [] } = useWorkspaceMembers(series?.home_workspace_id);

  // The face pool is the ASSIGNMENT pool, not the attendance roster — same union
  // MemoPage feeds its mention picker: workspace members ∪ memo participants ∪
  // series participants ∪ editors, deduped by user id. See lib/memoPeople.
  const members = useMemo<WorkspaceMember[]>(() => {
    const byId = new Map<string, WorkspaceMember>();
    for (const m of workspaceMembers) byId.set(m.user_id, m);
    const add = (u: MemoUser) => {
      if (byId.has(u.id)) return;
      byId.set(u.id, {
        user_id: u.id,
        role: "member",
        joined_at: "",
        email: "",
        display_name: u.display_name,
        username: u.username,
        avatar_url: u.avatar_url,
        department_workspace_id: u.department_workspace_id ?? null,
        department_color: u.department_color ?? null,
      });
    };
    (memo?.participants ?? []).forEach(add);
    (series?.participants ?? []).forEach(add);
    (series?.editors ?? []).forEach(add);
    return [...byId.values()];
  }, [workspaceMembers, memo?.participants, series?.participants, series?.editors]);

  const peopleById = useMemo(() => memoPeopleById(members), [members]);

  // Lean print: no cross-week history fold (see the doc comment above). An empty
  // map means `buildPrintDoc` renders each section's OWN entries only.
  const noHistory = useMemo(() => new Map<string, SectionHistoryEntry[]>(), []);

  const doc = useMemo(
    () => (memo ? buildPrintDoc(memo, peopleById, noHistory) : null),
    [memo, peopleById, noHistory],
  );

  const ready = !isLoading && !!memo;

  return { doc, ready, isError: false };
}
